import { useRef, type ReactNode } from 'react'
import { CarouselNav } from './CarouselNav'

/**
 * Generic horizontally-scrollable row used by the home screen.
 * Mirrors the desktop `CarouselView`'s row structure (heading +
 * optional subtitle + horizontal scroller with scroll-snap cards)
 * but trimmed down to the subset the home view needs.
 *
 * Unlike `DiscoverCarouselRow`, this component is purely layout: it
 * does not lazy-fetch anything. Each row in the home view already has
 * its data in-hand (library state + in-memory TMDB details), so the
 * row just needs to render the cards passed as children.
 *
 * Always renders — a row with zero children still shows a heading +
 * empty-state hint. That matches the desktop fidelity rule: missing
 * data never removes a row.
 */
export function HomeRow({
  title,
  subtitle,
  count,
  emptyHint,
  loading,
  children,
}: {
  title: string
  subtitle?: string
  count?: number
  emptyHint?: string
  loading?: boolean
  children: ReactNode
}) {
  const hasChildren = count != null ? count > 0 : true
  const scrollRef = useRef<HTMLDivElement>(null)
  return (
    <section className="relative" aria-label={title}>
      <header className="mb-2 flex items-baseline justify-between px-4">
        <div>
          <h2 className="font-inter text-base font-semibold text-text-primary sm:text-lg">
            {title}
          </h2>
          {subtitle && (
            <p className="text-[11px] text-text-tertiary sm:text-xs">{subtitle}</p>
          )}
        </div>
        {count != null && count > 0 && (
          <span className="text-[11px] text-text-tertiary">
            {count} title{count === 1 ? '' : 's'}
          </span>
        )}
      </header>

      <div className="relative">
        <div
          ref={scrollRef}
          role="list"
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 scrollbar-hide sm:gap-4"
          aria-busy={loading || undefined}
        >
          {hasChildren ? (
            children
          ) : loading ? (
            <HomeRowSkeletons />
          ) : (
            <div className="w-full py-8 text-center text-xs text-text-tertiary">
              {emptyHint ?? 'Nothing to show here yet.'}
            </div>
          )}
        </div>
        {hasChildren && <CarouselNav scrollRef={scrollRef} ariaLabel={title} />}
      </div>
    </section>
  )
}

function HomeRowSkeletons() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="shrink-0 snap-start"
          style={{ width: 'clamp(120px, 16vw, 180px)' }}
        >
          <div className="relative aspect-[2/3] animate-pulse overflow-hidden rounded-lg border border-border-primary bg-bg-tertiary">
            <div className="absolute inset-0 bg-gradient-to-r from-bg-tertiary via-bg-secondary to-bg-tertiary" />
          </div>
          <div className="mt-2 space-y-1">
            <div className="h-3 animate-pulse rounded bg-bg-tertiary" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-bg-tertiary" />
          </div>
        </div>
      ))}
    </>
  )
}
